/** `traverse.teleport` — paired transporters. Pads are entities carrying the
 * configured tag, paired in spawn order (1st↔2nd, 3rd↔4th, …). Pressing the
 * entry direction while standing on a pad HOLDS the body (the `heldBy`
 * protocol) for a charge, then fires it to the partner pad. After arriving
 * the traveller cannot re-enter until the lock expires AND the entry input
 * has been released. Publishes each pair as two solver navigation links. */
import { id, int, num, obj, oneOf } from '../schemaBuilders.js';
import { defineModule, num as read, str, type ModuleCtx } from '../module.js';
import { heldBy, supportSurface, surfaces } from '../physics.js';
import { deltaX, type EntityV1 } from '../world.js';
import { ANIM_STATE_NAMES, type AnimStateName } from '../../animStates.js';

export type TeleportConfig = {
  padTag: string;
  reach: number;
  chargeTicks: number;
  lockTicks: number;
  enter: 'up' | 'down' | 'either';
  /** Pose while charging. */
  state: AnimStateName;
};

/** The pad paired with `pad` (spawn order), if both exist. */
export function partnerPad(ctx: ModuleCtx<TeleportConfig>, pad: EntityV1): EntityV1 | undefined {
  const pads = ctx.entities({ tag: ctx.config.padTag });
  const i = pads.findIndex(p => p.id === pad.id);
  return i < 0 ? undefined : pads[i ^ 1];
}

export const teleport = defineModule<TeleportConfig>({
  id: 'traverse.teleport', version: 1, attach: 'entity', role: 'behaviour',
  summary: 'Paired transporters: enter a pad, charge in place, then fire to its partner pad; a lock prevents bouncing straight back.',
  config: obj({ padTag: id(), reach: num(0.05, 2), chargeTicks: int(0, 120), lockTicks: int(0, 120), enter: oneOf('up', 'down', 'either'),
    state: oneOf(...ANIM_STATE_NAMES) }),
  requires: ['body.platformer'], events: ['teleport-charge', 'teleport'],
  presentation: { 'teleport-charge': { cue: 'countdown' }, teleport: { cue: 'whoosh', vfx: { effect: 'burst', palette: 'slot', count: 8 } } },
  proofs: ['charge-then-fire-to-partner', 'paired-in-spawn-order', 'lock-and-release-before-reentry', 'unpaired-pad-inert', 'charge-state'],
  animStates: c => [c.state],
  init(ctx, e) { Object.assign(ctx.data(e), { hold: false, pad: null, timer: 0, lock: 0, armed: true }); },
  systems: {
    think(ctx, e) {
      if (!e) return;
      const c = ctx.config, d = ctx.data(e), world = ctx.world;
      if (e.status !== 'active') { d.hold = false; d.pad = null; return; }
      if (heldBy(e, 'traverse.teleport')) return;
      const input = ctx.input(e.slot ?? 0);
      const pressed = c.enter === 'either' ? input.dy !== 0 : input.dy === (c.enter === 'up' ? -1 : 1);
      if (!pressed) d.armed = true;
      if (read(d, 'lock') > 0) d.lock = read(d, 'lock') - 1;
      let pad = d.hold === true ? ctx.entity(str(d, 'pad')) : undefined;
      if (!pad) {
        d.hold = false; d.pad = null;
        if (!pressed || d.armed !== true || read(d, 'lock') > 0 || !e.grounded) return;
        pad = ctx.entities({ tag: c.padTag }).find(p => Math.abs(deltaX(world, p.x, e.x)) <= c.reach && Math.abs(e.y - p.y) <= 0.1
          && partnerPad(ctx, p) !== undefined);
        if (!pad) return;
        Object.assign(d, { hold: true, pad: pad.id, timer: c.chargeTicks });
        e.x = pad.x;
        ctx.emit('teleport-charge', { x: e.x, y: e.y, slot: e.slot ?? 0 });
      }
      e.vx = 0; e.vy = 0;
      d.timer = read(d, 'timer') - 1;
      if (read(d, 'timer') > 0) return;
      const exit = partnerPad(ctx, pad);
      Object.assign(d, { hold: false, pad: null, lock: c.lockTicks, armed: false });
      if (!exit) return;
      e.x = exit.x; e.y = exit.y;
      const ground = supportSurface(world, surfaces(ctx), e.x, e.w / 4, e.y);
      e.grounded = ground !== null; e.groundId = ground?.entity.id ?? null;
      ctx.emit('teleport', { x: e.x, y: e.y - e.h / 2, slot: e.slot ?? 0 });
    },
    present(ctx, e) {
      if (e && e.status === 'active' && ctx.data(e).hold === true) ctx.setAnim(e, ctx.config.state);
    },
  },
  navLinks(ctx, e, player) {
    if (!e || e.id !== player.id) return [];
    const cost = 1 + ctx.config.chargeTicks / 10;
    return ctx.entities({ tag: ctx.config.padTag }).flatMap(p => {
      const q = partnerPad(ctx, p);
      return q ? [{ from: { x: p.x, y: p.y }, to: { x: q.x, y: q.y }, cost, dy: ctx.config.enter === 'down' ? 1 as const : -1 as const, along: false, label: 'teleport' }] : [];
    });
  },
});
